"use client";

import { Analytics } from "@vercel/analytics/react";
import { useEffect, useRef, useState } from "react";
import {
  CONSENT_UPDATED_EVENT,
  clearConsentPromptSeen,
  clearTrackingConsentSnapshot,
  writeTrackingConsentSnapshot,
} from "@/lib/consent/client";
import { DEFAULT_CONSENT_PREFERENCES } from "@/lib/consent/defaults";
import type { ConsentPreferences, ConsentResponse } from "@/lib/consent/types";
import {
  initPostHog,
  resetPostHogSession,
} from "@/lib/posthog/client";
import { DEEPFACE_SESSION_STARTED_EVENT } from "@/lib/posthog/product-event-names";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";

export default function TelemetryProvider() {
  const [consent, setConsent] = useState<ConsentPreferences>({
    ...DEFAULT_CONSENT_PREFERENCES,
  });
  const [hasVerifiedServerConsent, setHasVerifiedServerConsent] = useState(false);
  const capturedSessionStartRef = useRef(false);

  useEffect(() => {
    let active = true;
    let syncRequestId = 0;
    let inFlightController: AbortController | null = null;
    const supabase = getSupabaseBrowserClient();

    const resetConsent = () => {
      if (!active) return;
      setConsent({ ...DEFAULT_CONSENT_PREFERENCES });
      setHasVerifiedServerConsent(false);
      resetPostHogSession();
      capturedSessionStartRef.current = false;
    };

    const syncFromServer = async () => {
      const requestId = ++syncRequestId;
      inFlightController?.abort();
      const controller = new AbortController();
      inFlightController = controller;
      try {
        const response = await fetch("/api/account/consents", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!active || requestId !== syncRequestId) return;
        if (!response.ok) {
          resetConsent();
          clearTrackingConsentSnapshot();
          return;
        }

        const payload = (await response.json()) as ConsentResponse;
        if (!active || requestId !== syncRequestId) return;
        setConsent((prev) => ({ ...prev, ...payload.preferences }));
        setHasVerifiedServerConsent(true);
        writeTrackingConsentSnapshot(payload.preferences);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }
        if (!active || requestId !== syncRequestId) return;
        resetConsent();
        clearTrackingConsentSnapshot();
        // Keep telemetry disabled until server consent can be verified.
      } finally {
        if (inFlightController === controller) {
          inFlightController = null;
        }
      }
    };

    const syncForCurrentSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!active) return;
        if (!session) {
          resetConsent();
          clearTrackingConsentSnapshot();
          return;
        }
        await syncFromServer();
      } catch {
        if (!active) return;
        resetConsent();
        clearTrackingConsentSnapshot();
      }
    };

    void syncForCurrentSession();

    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        syncRequestId += 1;
        inFlightController?.abort();
        inFlightController = null;
        resetConsent();
        clearTrackingConsentSnapshot();
        clearConsentPromptSeen();
        return;
      }
      if (
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED"
      ) {
        void syncFromServer();
      }
    });

    return () => {
      active = false;
      inFlightController?.abort();
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const onConsentUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<ConsentPreferences>;
      if (!customEvent.detail) return;
      setConsent((prev) => ({ ...prev, ...customEvent.detail }));
      setHasVerifiedServerConsent(true);
    };
    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated as EventListener);
    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!hasVerifiedServerConsent) {
      return;
    }
    const client = initPostHog({
      analytics_tracking: consent.analytics_tracking,
      session_replay: consent.session_replay,
    });
    if (!client || !consent.analytics_tracking) {
      return;
    }
    if (capturedSessionStartRef.current) {
      return;
    }
    client.capture(DEEPFACE_SESSION_STARTED_EVENT, {
      surface: "website",
      session_replay: consent.session_replay,
    });
    capturedSessionStartRef.current = true;
  }, [
    hasVerifiedServerConsent,
    consent.analytics_tracking,
    consent.session_replay,
  ]);

  if (!hasVerifiedServerConsent || !consent.analytics_tracking) {
    return null;
  }

  return <Analytics />;
}
